import React from 'react'
import { AuthContext } from 'auth/Auth'
import {
    UserPermission,
    extractPermissions,
    MyIDUser,
    RoleName,
} from '@genome-web-forms/common/auth'

export const useHasRole = (role: string): boolean => {
    const { user } = React.useContext(AuthContext)
    const roles = user.roles || []
    const roleNames = roles.map(r => r.name)
    return React.useMemo(() => roleNames.includes(role), [roleNames, role])
}

export const useHasPortrayalsEditingRoles = (): boolean => {
    const hasDEIRole = useHasRole(RoleName.DEI)
    const hasLibReadRole = useHasRole(RoleName.LIBRARY_READ)
    const hasLibWriteRole = useHasRole(RoleName.LIBRARY_WRITE)
    return hasDEIRole || (hasLibReadRole && hasLibWriteRole)
}

export const useHasDEIRole = (): boolean => {
    const hasDEIRole = useHasRole(RoleName.DEI)
    return hasDEIRole
}

export const useHasLibReadRole = (): boolean => {
    return useHasRole(RoleName.LIBRARY_READ)
}

export const useHasLibWriteRole = (): boolean => {
    return useHasRole(RoleName.LIBRARY_WRITE)
}

export const useHasRoles = (): boolean => {
    const { user } = React.useContext(AuthContext)
    return !!user.roles && !!user.roles.length
}

export const useHasPermission = (permission: UserPermission): boolean => {
    const auth = React.useContext(AuthContext)

    return React.useMemo(
        () => (auth.user ? hasPermission(auth.user, permission) : false),
        [auth.user, permission],
    )
}

export const hasPermission = (user: MyIDUser, permission: string): boolean => {
    return extractPermissions(user).includes(permission)
}

export const hasRoles = (user: MyIDUser): boolean => {
    return !!user.roles && !!user.roles.length
}

export type UserHasPermissionProps = {
    permission: UserPermission
}

export const UserHasPermission: React.FC<UserHasPermissionProps> = ({
    permission,
    children,
}): React.ReactElement | null => {
    return useHasPermission(permission) ? (children as React.ReactElement) : null
}

export default useHasPermission
